<template>
    <div>
        <v-card class="ordering-devices" outlined>
            <v-card-title class="page-heading primary">
                <v-row align="center">
                    <v-col cols="12" sm="9">{{ $tc('general.terminal', 2) }}</v-col>
                </v-row>
            </v-card-title>

            <v-card-text>
                <v-data-table
                    class="items-table operations striped"
                    :headers="headers"
                    :items="items"
                    :footer-props="{ 'items-per-page-options': [20, 50, 100, 200] }"
                    :loading="loading"
                    :mobile-breakpoint="0"
                    dense
                >
                    <template v-slot:item="{ item }">
                        <tr :class="{ highlight: terminal && item.id === terminal?.id }">
                            <td>
                                <span>{{ item.name }}</span>
                                <span v-if="item.type === 'orderingDevice'">
                                    / {{ item.cash_register?.getFullName() || '' }}
                                </span>
                            </td>
                            <td>
                                <span v-if="item.type === 'cashRegister'">{{ $t('terminals.cash_register') }}</span>
                                <span v-else-if="item.type === 'orderingDevice'">{{ $t('terminals.ordering_device') }}</span>
                                <span v-else-if="item.type === 'kitchenDisplaySystem'">{{ $t('terminals.kitchen_display_system') }}</span>
                            </td>
                            <td>{{ item.platform }}</td>
                            <td>{{ item.model }}</td>
                            <td>{{ item.manufacturer }}</td>
                            <td align="center" class="w35 maxw60">
                                <v-menu bottom transition="slide-y-transition">
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" icon>
                                            <v-icon>far fa-ellipsis-h</v-icon>
                                        </v-btn>
                                    </template>

                                    <v-list>
                                        <v-list-item
                                            key="edit"
                                            @click="openForm(item)"
                                        >
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon>fal fa-pen</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ $t('general.edit') }}</v-list-item-title>
                                        </v-list-item>

                                        <v-divider />

                                        <v-list-item
                                            key="delete"
                                            :disabled="item.type === 'cashRegister'"
                                            @click="deleteItem(item)"
                                        >
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon
                                                    :disabled="item.type === 'cashRegister'"
                                                    :class="{'error--text': item.type !== 'cashRegister' }"
                                                >
                                                    fal fa-times
                                                </v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title :class="{'error--text': item.type !== 'cashRegister' }">
                                                {{ $t('general.delete') }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <!-- Cash Sale Edit Dialog -->
        <terminal-form
            :show.sync="showForm"
            :item="selectedItem"
            :cash-registers="cashRegisters"
            @save="onItemSaved"
        />
    </div>
</template>

<script>
import { loadData } from '@/views/shared/loadPageData';
import ApiService from '@/services/ApiService';
import TerminalForm from '@/views/shared/admin/terminals/TerminalForm.vue';
import CashRegisterService from '@/services/CashRegisterService';
import TerminalService from '@/services/TerminalService';
import CashRegister from '@/models/CashRegister';
import Terminal from '@/models/Terminal';

export default {
    components: {
        TerminalForm
    },

    async created() {
        loadData('/admin/load/terminals').then(data => this.onDataLoaded(data));
        this.terminal = await Terminal.current()
    },

    data() {
        return {
            items: [],
            itemsTotal: 0,
            loading: false,

            showForm: false,
            selectedItem: null,

            headers: [
                { text: this.$t('terminals.name'), value: 'name' },
                { text: this.$t('terminals.type'), value: 'cashRegister.name' },
                { text: this.$t('terminals.platform'), value: 'platform' },
                { text: this.$t('terminals.model'), value: 'model' },
                { text: this.$t('terminals.manufacturer'), value: 'manufacturer' },
                { text: '', sortable: false }
            ],

            cashRegisters: [],
            terminal: null
        };
    },

    computed: {
        allowDeviceRegistration() {
            if(this.device.platform !== 'android' && this.device.platform !== 'ios') return false;

            return !this.items.some(item => item.uuid === this.device.uuid)
        }
    },

    methods: {
        onDataLoaded(data) {
            this.items = data.terminals.map(d => {
                d.cash_register = d.cash_register ? new CashRegister(d.cash_register) : null
                return d
            })
            this.cashRegisters = data.cash_registers.map(c => new CashRegister(c))
        },

        openForm(item) {
            this.selectedItem = item;
            this.showForm = true;
        },

        async onItemSaved(item) {
            this.showForm = false;

            const updatedItemIndex = this.items.findIndex(i => i.id === item.id)
            if(updatedItemIndex !== -1) {
                this.$set(this.items, updatedItemIndex, item);
            }

            this.selectedItem = null;
        },

        deleteItem(item) {
            this.$confirm(
                this.$t('terminals.permanently_delete'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    icon: 'fal fa-times',
                    color: 'error',
                    title: this.$t('general.delete')
                },
                async result => {
                    if (!result) return;

                    await ApiService.delete(`/admin/terminals/${item.id}`, {})

                    this.items = this.items.filter(i => i.id !== item.id);
                    this.$snackbar(this.$t('terminals.deleted'), 'success');
                    this.itemsTotal--;

                    if(item.id === this.terminal.id) {
                        CashRegisterService.removeCashRegister()
                        TerminalService.removeTerminal()
                    }
                }
            );
        },
    }
};
</script>

<style lang="scss" scoped>
.ordering-devices {
    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }

    ::v-deep .items-table {
        margin-top: 28px;

        th {
            font-size: 11px;
            padding: 0 4px;
            max-width: 80px;

            span {
                white-space: break-spaces;
            }
        }

        td {
            font-size: 13px;
            padding: 0 4px;
        }

        .separator {
            border-left: 1px solid rgba(0, 0, 0, 0.5);
            padding-left: 12px;
        }

        tfoot {
            font-weight: 500;
            background: #f3f4fb;
        }
    }

    .highlight {
        color: var(--v-primary-base);
    }
}
</style>
